import { useEffect, useRef } from 'react';
import { customerPortalAPI } from '../api';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  open_tickets: 'support ticket',
  pending_warranties: 'warranty registration',
  pending_bookings: 'meeting request',
  waiting_chats: 'live chat',
  pending_files: 'file upload',
  pending_testimonials: 'testimonial',
};

/**
 * StaffActivityNotifier - out-of-tab awareness for the team.
 * Polls pending customer-portal activity and raises a desktop (OS) notification
 * + a short sound chime whenever a new item arrives, even if the CRM tab is in
 * the background. No UI; mounted once in the staff layout.
 */
export const StaffActivityNotifier = () => {
  const prev = useRef(null);
  const enabled = useRef(false);
  const pollRef = useRef(null);

  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      osc.start();
      osc.stop(ctx.currentTime + 0.36);
    } catch (e) { /* ignore */ }
  };

  const notify = (title, body) => {
    playChime();
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const n = new Notification(title, { body, tag: 'chinook-activity', icon: '/favicon.ico' });
        n.onclick = () => { window.focus(); window.location.href = '/my-dashboard'; n.close(); };
      } else {
        // In-app fallback
        toast.message(title, { description: body });
      }
    } catch (e) {
      toast.message(title, { description: body });
    }
  };

  const poll = async () => {
    try {
      const res = await customerPortalAPI.adminGetPendingCounts();
      const data = res.data;
      if (prev.current) {
        const increases = [];
        Object.keys(CATEGORY_LABELS).forEach((k) => {
          const before = prev.current[k] || 0;
          const now = data[k] || 0;
          if (now > before) increases.push({ key: k, delta: now - before });
        });
        if (increases.length === 1) {
          const i = increases[0];
          notify('New customer activity', `${i.delta} new ${CATEGORY_LABELS[i.key]}${i.delta > 1 ? 's' : ''}`);
        } else if (increases.length > 1) {
          const total = increases.reduce((s, i) => s + i.delta, 0);
          notify('New customer activity', `${total} new items need attention`);
        }
      }
      prev.current = data;
      enabled.current = true;
    } catch (e) {
      // 403 for non-staff -> stop polling
      if (e.response && e.response.status === 403) {
        if (pollRef.current) clearInterval(pollRef.current);
      }
    }
  };

  useEffect(() => {
    // Ask for desktop notification permission once
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    poll(); // establish baseline (no alert on first load)
    pollRef.current = setInterval(poll, 20000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  return null;
};

export default StaffActivityNotifier;
